import { CommonModule } from '@angular/common';
import { Component, computed, Input, model, TemplateRef } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AVATAR_WIDTH } from '../../../../constants';
import { TranslationModule } from '../../../../i18n/translation-module';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { ActivityInterface } from '@vendasta/crm';
import { RxState } from '@rx-angular/state';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { CrmFieldService, StandardIds, SystemFieldIds } from '../../../../shared-services/crm-services/field.service';
import { User } from '@vendasta/iamv2';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';

import { ActivityAssociationService } from '../../activities/activity-association.service';
import { RouterLink } from '@angular/router';
import { ActivityIcons, ActivityTypes } from '../../activities';

const ActivityHeaderBaseKeys = {
  [ActivityTypes.Note]: 'ACTIVITY.LOGGED_ACTIVITY.HEADER_ADDITIONAL_TEXT.NOTE',
  [ActivityTypes.Meeting]: 'ACTIVITY.LOGGED_ACTIVITY.HEADER_ADDITIONAL_TEXT.MEETING',
  [ActivityTypes.Task]: 'ACTIVITY.LOGGED_ACTIVITY.HEADER_ADDITIONAL_TEXT.TASK',
  [ActivityTypes.Email]: 'ACTIVITY.LOGGED_ACTIVITY.HEADER_ADDITIONAL_TEXT.EMAIL',
  [ActivityTypes.Call]: 'ACTIVITY.LOGGED_ACTIVITY.HEADER_ADDITIONAL_TEXT.CALL',
  [ActivityTypes.Communication]: 'ACTIVITY.LOGGED_ACTIVITY.HEADER_ADDITIONAL_TEXT.COMMUNICATION',
};

const DefaultHeaderPreposition = 'ACTIVITY.LOGGED_ACTIVITY.HEADER_ADDITIONAL_TEXT.BY';
const TaskHeaderPreposition = 'ACTIVITY.LOGGED_ACTIVITY.HEADER_ADDITIONAL_TEXT.TO';

@Component({
  selector: 'crm-activity-template-v2',
  templateUrl: './activity-template-v2.component.html',
  styleUrls: ['./activity-template-v2.component.scss'],
  providers: [RxState],
  imports: [
    CommonModule,
    TranslateModule,
    TranslationModule,
    MatCardModule,
    MatIconModule,
    GalaxyAvatarModule,
    RouterLink,
  ],
})
export class CrmActivityTemplateV2Component {
  AVATAR_WIDTH = AVATAR_WIDTH;
  dateFormat = DateFormat.mediumDateShortTime;
  unknownNamePlaceholder = this.translateService.instant('CONTACT.ACTIVITIES.UNKNOWN_NAME');

  activity = model<ActivityInterface>({});
  ownerUser = model<User | undefined>(undefined);
  isOwnedByServiceAccount = model<boolean>(false);
  showAvatar = model<boolean>(true);

  // custom template to render in the subtitle section, replacing the formatted date
  @Input() subtitleTemplate?: TemplateRef<unknown>;

  showPrimaryAssociation = model<boolean>(false);

  fullName = computed(() => {
    const user = this.ownerUser() as User;
    return `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || this.unknownNamePlaceholder;
  });

  readonly headerText = computed(() => this.getHeaderText(this.activity()?.crmObjectSubtype));

  readonly effectiveDate = computed(() => {
    return (
      this.fieldService.getFieldValueFromCrmObject(this.activity(), SystemFieldIds.ActivityEffectiveDate)?.dateValue ??
      null
    );
  });

  primaryAssociation = this.associationService.primaryAssociation;
  routePrefix = this.associationService.routePrefix;

  activityIcon = computed(() => {
    const activityType = this.activity()?.crmObjectSubtype;
    if (!activityType) return;
    return ActivityIcons[activityType];
  });

  constructor(
    private readonly fieldService: CrmFieldService,
    private readonly translateService: TranslateService,
    private readonly associationService: ActivityAssociationService,
  ) {}

  private getHeaderText(activityType?: string): ActivityHeaderParts | undefined {
    if (!activityType) return;
    const translationKey = this.headerTextFromActivityType(activityType);
    return {
      typeWithVerb: translationKey,
      preposition: activityType === ActivityTypes.Task ? TaskHeaderPreposition : DefaultHeaderPreposition,
    };
  }

  private headerTextFromActivityType(activityType: string): string {
    const baseKey = ActivityHeaderBaseKeys[activityType];
    if (!baseKey) return '';

    switch (activityType) {
      case ActivityTypes.Email: {
        const emailDirection = this.getStringFieldData(this.activity(), StandardIds.ActivityEmailDirection);
        return `${baseKey}.${emailDirection.toUpperCase()}`;
      }
      case ActivityTypes.Call: {
        const callDirection = this.getStringFieldData(this.activity(), StandardIds.ActivityCallDirection);
        return `${baseKey}.${callDirection.toUpperCase()}`;
      }
      case ActivityTypes.Communication: {
        const communicationChannel = this.getStringFieldData(this.activity(), StandardIds.ActivityCommunicationChannel);
        return `${baseKey}.${communicationChannel.toUpperCase()}`;
      }
      default:
        return baseKey;
    }
  }

  private getStringFieldData(activity: ActivityInterface, fieldId: string): string {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.stringValue ?? '';
  }
}

interface ActivityHeaderParts {
  typeWithVerb: string;
  preposition?: string;
}
